import * as React from "react";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
  RouteProps,
  RouteComponentProps,
  useHistory,
} from "react-router-dom";

import "react-datepicker/dist/react-datepicker.css";

import styles from "./ManagerApp.module.scss";
import warning from "./assets/images/warning.png";
import { diff } from "./libs/Util";
import { useReducerWithEnhancedDispatch } from "./libs/enhancedDispatch";
import ModalProvider from "./libs/ModalProvider";
import ScrollToTop from "./libs/ScrollToTop";
import {
  reducer,
  Context,
  logout,
  fetchInitialData,
  InitialAppState,
  AppState,
  Actions,
  loginPath,
  prefixPath,
  teachersPath,
  classesPath,
  nextYearClassesPath,
  studentsPath,
  nextYearStudentsPath,
  bulkEditStudentsPath,
  bulkEditNextYearStudentsPath,
  newTeacherPath,
  newClassPath,
  newNextYearClassPath,
  editClassPath,
  editNextYearClassPath,
  editTeacherPath,
  bulkRegisterTeachersPath,
  newStudentPath,
  bulkRegisterStudentsPath,
  bulkRegisterNextYearStudentsPath,
  newNextYearStudentPath,
  editStudentPath,
  editNextYearStudentPath,
  notificationsPath,
  newNotificationPath,
  copyNotificationPath,
  editNotificationPath,
  bulkEditClassesPath,
  bulkEditNextYearClassesPath,
  gearStatusPath,
  subClassesPath,
  newSubClassPath,
  editSubClassPath,
  editSubClassStudentPath,
  exchangeSchoolYearPath,
  checkSchoolStatus,
  clearSuccessMessage,
  EXCHANGE_YEAR_NOW_UPDATED_SUCCESS_MESSAGE,
  authenticationPath,
  LOGOUT_SUCCESS_MESSAGE,
  useHistoriesPath,
  uploadStudentRealPhotosPath,
  subjectsPath,
  subjectRegistrationPath,
  bulkRegisterTeacherSubjectsPath,
} from "./managerStore";
import { homePath } from "./managerStore";
import Modal from "./atoms/Modal";
import Toast from "./molecules/Toast";
import LoginHeader from "./organisms/manager/Header/LoginHeader";
import LogoutHeader from "./organisms/manager/Header/LogoutHeader";
import SideMenuBar from "./organisms/manager/SideMenuBar";
import Error404Page from "./templates/Error404Page";
import Error503Page from "./templates/Error503Page";
import HomePage from "./pages/manager/HomePage";
import Teachers from "./pages/manager/Teachers";
import Classes from "./pages/manager/Classes";
import NextYearClasses from "./pages/manager/NextYearClasses";
import NewTeacher from "./pages/manager/NewTeacher";
import NewClass from "./pages/manager/NewClass";
import NewNextYearClass from "./pages/manager/NewNextYearClass";
import EditTeacher from "./pages/manager/EditTeacher";
import BulkRegisterTeachers from "./pages/manager/BulkRegisterTeachers";
import Login from "./pages/manager/Login";
import MultiFactorAuthentication from "./pages/manager/MultiFactorAuthentication";
import EditClass from "./pages/manager/EditClass";
import EditNextYearClass from "./pages/manager/EditNextYearClass";
import Students from "./pages/manager/Students";
import NextYearStudents from "./pages/manager/NextYearStudents";
import NewStudent from "./pages/manager/NewStudent";
import NewNextYearStudent from "./pages/manager/NewNextYearStudent";
import EditStudent from "./pages/manager/EditStudent";
import EditNextYearStudent from "./pages/manager/EditNextYearStudent";
import BulkEditStudents from "./pages/manager/BulkEditStudents";
import UseHistories from "./pages/manager/UseHistories";
import BulkEditNextYearStudents from "./pages/manager/BulkEditNextYearStudents";
import BulkRegisterNextYearStudents from "./pages/manager/BulkRegisterNextYearStudents";
import StatusUtilization from "./pages/manager/StatusUtilization";
import Notifications from "./pages/manager/Notifications";
import NewNotification from "./pages/manager/NewNotification";
import CopyNotification from "./pages/manager/CopyNotification";
import EditNotification from "./pages/manager/EditNotification";
import BulkEditClasses from "./pages/manager/BulkEditClasses";
import BulkEditNextYearClasses from "./pages/manager/BulkEditNextYearClasses";
import SubClasses from "./pages/manager/SubClasses";
import NewSubClass from "./pages/manager/NewSubClass";
import EditSubClass from "./pages/manager/EditSubClass";
import EditSubClassStudent from "./pages/manager/EditSubClassStudent";
import cookie, { cookieNames } from "./libs/cookie";
import ExchangeSchoolYear from "./pages/manager/ExchangeSchoolYear";
import UploadStudentRealPhotosPage from "./pages/manager/UploadStudentRealPhotos";
import BulkRegisterStudents from "./pages/manager/BulkRegisterStudents";
import SubjectsPage from "./pages/manager/SubjectsPage";
import SubjectRegistrationPage from "./pages/manager/SubjectRegistrationPage";
import BulkRegisterTeacherSubjects from "./pages/manager/BulkRegisterTeacherSubjects";

interface IConditionalProps extends RouteProps {
  readonly isMatch: boolean;
  readonly redirectPath: string;
  readonly component: React.ComponentClass<any> | React.StatelessComponent<any>;
}

const nextYearRoutes = [
  {
    path: nextYearClassesPath,
    component: NextYearClasses,
  },
  {
    path: newNextYearClassPath,
    component: NewNextYearClass,
  },
  {
    path: bulkEditNextYearClassesPath,
    component: BulkEditNextYearClasses,
  },
  {
    path: `${editNextYearClassPath}/:id`,
    component: EditNextYearClass,
  },
  {
    path: nextYearStudentsPath,
    component: NextYearStudents,
  },
  {
    path: newNextYearStudentPath,
    component: NewNextYearStudent,
  },
  {
    path: `${editNextYearStudentPath}/:id`,
    component: EditNextYearStudent,
  },
  {
    path: bulkEditNextYearStudentsPath,
    component: BulkEditNextYearStudents,
  },
  {
    path: bulkRegisterNextYearStudentsPath,
    component: BulkRegisterNextYearStudents,
  },
  {
    path: exchangeSchoolYearPath,
    component: ExchangeSchoolYear,
  },
];

const ConditionalRoute = ({
  component,
  redirectPath,
  isMatch,
  ...rest
}: IConditionalProps) => {
  const Component = component;

  const render = (renderProps: RouteComponentProps<any>) => {
    let element = <Component {...renderProps} />;

    if (!isMatch) {
      element = (
        <Redirect
          to={{
            pathname: redirectPath,
            state: { from: renderProps.location },
          }}
        />
      );
    }

    return element;
  };
  return <Route {...rest} render={render} />;
};

const reducerForApp =
  process.env.NODE_ENV === "production"
    ? reducer
    : (state: AppState, action: Actions) => {
        console.groupCollapsed(action.types);
        console.log("action", action);
        const result = reducer(state, action);
        console.log("diff", diff(state, result));
        console.log("prevState", state);
        console.log("currentState", result);
        console.groupEnd();
        return result;
      };

interface ISwitchYearProps {
  state: any;
  dispatch: React.Dispatch<any>;
  children: React.ReactNode;
}

const SwitchYear: React.FC<ISwitchYearProps> = ({
  state,
  dispatch,
  children,
}) => {
  const schoolStatusInterval = React.useRef<ReturnType<typeof setInterval>>();
  const history = useHistory();

  React.useEffect(() => {
    if (state.manager.school.change_current_year_status === "inprogress") {
      schoolStatusInterval.current = setInterval(() => {
        dispatch(checkSchoolStatus());
      }, 5000);
    }
    if (state.success_message === EXCHANGE_YEAR_NOW_UPDATED_SUCCESS_MESSAGE) {
      dispatch(clearSuccessMessage());
      schoolStatusInterval.current &&
        clearInterval(schoolStatusInterval.current);
      history.push(homePath);
      Toast.success(state.success_message, { autoClose: 5000 });
    }

    return () =>
      schoolStatusInterval.current &&
      clearInterval(schoolStatusInterval.current);
  });

  return <>{children}</>;
};

const redirectPath = new URLSearchParams(window.location.search).get(
  "redirect_to",
);
const SESSION_KEY = "manager_session_id";

export const ManagerApp = (props: any) => {
  if (window.location.href.match(/session_id=/)) {
    const result = window.location.href.split("session_id=").pop() as string;
    const sessionID = result.replace(/&.*$/, "");
    sessionStorage.setItem(SESSION_KEY, sessionID);
  }
  const savedSessionId =
    sessionStorage.getItem(SESSION_KEY) ||
    cookie.getCookie(cookieNames.teacher_session_id) ||
    "";
  const isMasqueradeByAdmin = savedSessionId.startsWith("admin-");
  const [state, dispatch] = useReducerWithEnhancedDispatch({
    reducer: reducerForApp,
    initialAppState: {
      ...InitialAppState,
      session_id: savedSessionId,
      isMasqueradeByAdmin,
    },
  });

  const logoutFunc = React.useCallback(() => {
    cookie.deleteAll();
    sessionStorage.clear();
    dispatch(logout());
  }, [dispatch]);

  React.useEffect(() => {
    if (
      state.success_message === LOGOUT_SUCCESS_MESSAGE ||
      state.expires < new Date()
    ) {
      window.location.reload();
    }
  }, [state]);

  React.useEffect(() => {
    if (!state.session_id) return;

    if (window.location.href.match(/session_id=/)) {
      window.history.pushState(null, "", window.location.pathname);
    }

    redirectPath &&
    redirectPath.startsWith("/") &&
    !redirectPath.startsWith("//")
      ? window.location.assign(redirectPath)
      : dispatch(fetchInitialData());
  }, [state.session_id, dispatch]);

  React.useEffect(() => {
    if (savedSessionId && !state.session_id) {
      cookie.deleteAll();
      sessionStorage.removeItem(SESSION_KEY);
    } else if (!savedSessionId && state.session_id) {
      cookie.set(cookieNames.teacher_session_id, state.session_id, 7);
    }
    //  eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedSessionId, state.session_id]);

  return state.isServiceUnavailable ? (
    <Error503Page />
  ) : (
    <Context.Provider value={{ state, dispatch }}>
      <ModalProvider>
        <ScrollToTop {...props}>
          <Router basename={prefixPath}>
            <SwitchYear state={state} dispatch={dispatch}>
              {!state.session_id ? (
                <div style={{ height: "100vh" }} className={styles.body}>
                  <LogoutHeader />
                  <Switch>
                    <Route path={loginPath} component={Login} />
                    <Route
                      exact
                      path={`${authenticationPath}/:ott`}
                      component={MultiFactorAuthentication}
                    />
                    <Redirect
                      to={{
                        pathname: loginPath,
                      }}
                    />
                  </Switch>
                </div>
              ) : !state.manager.id ? (
                <div className={styles.body}>
                  <LoginHeader school_name="" />
                  <div className={styles.main} />
                </div>
              ) : (
                <div className={styles.body}>
                  <LoginHeader school_name={state.manager?.school?.name} />
                  <div className={styles.main}>
                    <SideMenuBar logout={logoutFunc} />
                    <div className={styles.content}>
                      <Switch>
                        <ConditionalRoute
                          path={gearStatusPath}
                          exact={true}
                          component={StatusUtilization}
                          redirectPath={loginPath}
                          isMatch={state.session_id}
                        />
                        <ConditionalRoute
                          path={homePath}
                          exact={true}
                          component={HomePage}
                          redirectPath={loginPath}
                          isMatch={state.session_id}
                        />
                        <ConditionalRoute
                          path={notificationsPath}
                          exact={true}
                          component={Notifications}
                          redirectPath={loginPath}
                          isMatch={state.session_id}
                        />
                        <ConditionalRoute
                          path={newNotificationPath}
                          exact={true}
                          component={NewNotification}
                          redirectPath={loginPath}
                          isMatch={state.session_id}
                        />
                        <ConditionalRoute
                          path={`${copyNotificationPath}/:id`}
                          component={CopyNotification}
                          redirectPath={loginPath}
                          isMatch={state.session_id}
                        />
                        <ConditionalRoute
                          path={`${editNotificationPath}/:id`}
                          component={EditNotification}
                          redirectPath={loginPath}
                          isMatch={state.session_id}
                        />
                        <ConditionalRoute
                          path={teachersPath}
                          component={Teachers}
                          redirectPath={loginPath}
                          isMatch={state.session_id}
                        />
                        <ConditionalRoute
                          path={newTeacherPath}
                          component={NewTeacher}
                          redirectPath={loginPath}
                          isMatch={state.session_id}
                        />
                        <ConditionalRoute
                          path={studentsPath}
                          component={Students}
                          redirectPath={loginPath}
                          isMatch={state.session_id}
                        />
                        <ConditionalRoute
                          path={newStudentPath}
                          component={NewStudent}
                          redirectPath={loginPath}
                          isMatch={state.session_id}
                        />
                        <ConditionalRoute
                          path={bulkRegisterStudentsPath}
                          component={BulkRegisterStudents}
                          redirectPath={loginPath}
                          isMatch={state.session_id}
                        />
                        <ConditionalRoute
                          path={`${editStudentPath}/:id`}
                          component={EditStudent}
                          redirectPath={loginPath}
                          isMatch={state.session_id}
                        />
                        <ConditionalRoute
                          path={`${bulkEditStudentsPath}`}
                          component={BulkEditStudents}
                          redirectPath={loginPath}
                          isMatch={state.session_id}
                        />
                        <ConditionalRoute
                          path={useHistoriesPath}
                          component={UseHistories}
                          redirectPath={loginPath}
                          isMatch={state.session_id}
                        />
                        <ConditionalRoute
                          path={bulkEditClassesPath}
                          component={BulkEditClasses}
                          redirectPath={loginPath}
                          isMatch={state.session_id}
                        />
                        <ConditionalRoute
                          path={`${editTeacherPath}/:id`}
                          component={EditTeacher}
                          redirectPath={loginPath}
                          isMatch={state.session_id}
                        />
                        <ConditionalRoute
                          path={bulkRegisterTeachersPath}
                          component={BulkRegisterTeachers}
                          redirectPath={loginPath}
                          isMatch={state.session_id}
                        />
                        <ConditionalRoute
                          path={newClassPath}
                          component={NewClass}
                          redirectPath={loginPath}
                          isMatch={state.session_id}
                        />
                        <ConditionalRoute
                          path={`${editClassPath}/:id`}
                          component={EditClass}
                          redirectPath={loginPath}
                          isMatch={state.session_id}
                        />
                        <ConditionalRoute
                          path={classesPath}
                          component={Classes}
                          redirectPath={loginPath}
                          isMatch={state.session_id}
                        />
                        <ConditionalRoute
                          path={newSubClassPath}
                          component={NewSubClass}
                          redirectPath={loginPath}
                          isMatch={state.session_id}
                        />
                        <ConditionalRoute
                          path={`${editSubClassPath}/:id`}
                          component={EditSubClass}
                          redirectPath={loginPath}
                          isMatch={state.session_id}
                        />
                        <ConditionalRoute
                          path={`${editSubClassStudentPath}/:id`}
                          component={EditSubClassStudent}
                          redirectPath={loginPath}
                          isMatch={state.session_id}
                        />
                        <ConditionalRoute
                          path={subClassesPath}
                          component={SubClasses}
                          redirectPath={loginPath}
                          isMatch={state.session_id}
                        />
                        <ConditionalRoute
                          path={subjectsPath}
                          component={SubjectsPage}
                          redirectPath={loginPath}
                          isMatch={state.session_id}
                        />
                        <ConditionalRoute
                          path={`${subjectRegistrationPath}/:teacher_id`}
                          component={SubjectRegistrationPage}
                          redirectPath={loginPath}
                          isMatch={state.session_id}
                        />
                        <ConditionalRoute
                          path={bulkRegisterTeacherSubjectsPath}
                          component={BulkRegisterTeacherSubjects}
                          redirectPath={loginPath}
                          isMatch={state.session_id}
                        />
                        <ConditionalRoute
                          path={uploadStudentRealPhotosPath}
                          component={UploadStudentRealPhotosPage}
                          redirectPath={loginPath}
                          isMatch={state.session_id}
                        />
                        {state.manager.school.change_current_year_status ===
                          "open" &&
                          nextYearRoutes.map((r, i) => (
                            <ConditionalRoute
                              key={i}
                              path={r.path}
                              component={r.component}
                              redirectPath={loginPath}
                              isMatch={state.session_id}
                            />
                          ))}
                        <ConditionalRoute
                          path={loginPath}
                          component={Login}
                          redirectPath={homePath}
                          isMatch={!state.session_id}
                        />
                        <ConditionalRoute
                          path={`${authenticationPath}/:ott`}
                          component={MultiFactorAuthentication}
                          redirectPath={homePath}
                          isMatch={!state.session_id}
                        />
                        <Route component={Error404Page} />
                      </Switch>
                    </div>
                  </div>
                </div>
              )}
            </SwitchYear>
          </Router>
        </ScrollToTop>
      </ModalProvider>
      {state.manager.school.change_current_year_status === "inprogress" && (
        <Modal blocked>
          <div className={styles.yearSwitching}>
            <img src={warning} alt="warning" />
            <p>年度更新中、しばらくお待ちください</p>
          </div>
        </Modal>
      )}
    </Context.Provider>
  );
};

export default ManagerApp;
